import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import { getExams, deleteExam, publishExam } from "../../api/examApi";
import { useAuth } from "../../common/contexts/AuthContext";

const ExamListPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleName = user?.role?.name || user?.Role?.name;
  const canManage = roleName === "Admin" || roleName === "Teacher" || roleName === "SuperAdmin";

  const refresh = async () => {
    try {
      setLoading(true);
      const data = await getExams();
      if (data?.success) setItems(data.items || []);
    } catch (err) {
      console.error('Failed to load exams:', err);
      toast.error(`Failed to load exams: ${err.response?.status ?? err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleDelete = (row) => {
    confirmDialog({
      message: `Delete exam "${row.name}"?`,
      header: "Confirm",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        await deleteExam(row.id);
        refresh();
      },
    });
  };

  const handlePublish = async (row) => {
    await publishExam(row.id);
    toast.success("Exam published");
    refresh();
  };

  const dateTemplate = (row) =>
    row.exam_date ? new Date(row.exam_date).toLocaleDateString() : "—";

  const publishedTemplate = (row) => (
    <Tag
      value={row.is_published ? "published" : "draft"}
      severity={row.is_published ? "success" : "warning"}
    />
  );

  const actionsTemplate = (row) => (
    <div className="flex gap-2">
      {canManage && (
        <>
          <Button
            icon="pi pi-list"
            className="p-button-text p-button-rounded p-button-sm"
            tooltip="Enter Results"
            onClick={() => navigate(`/index/exams/${row.id}/results`)}
          />
          <Button
            icon="pi pi-pencil"
            className="p-button-text p-button-rounded p-button-sm"
            tooltip="Edit"
            onClick={() => navigate(`/index/exams/${row.id}/edit`)}
          />
          {!row.is_published && (
            <Button
              icon="pi pi-send"
              className="p-button-text p-button-rounded p-button-sm p-button-success"
              tooltip="Publish"
              onClick={() => handlePublish(row)}
            />
          )}
          <Button
            icon="pi pi-trash"
            className="p-button-text p-button-rounded p-button-sm p-button-danger"
            tooltip="Delete"
            onClick={() => handleDelete(row)}
          />
        </>
      )}
    </div>
  );

  return (
    <Card>
      <ConfirmDialog />
      <div className="flex justify-content-between align-items-center mb-3">
        <h2 className="m-0">Exams</h2>
        {canManage && (
          <Button label="New Exam" icon="pi pi-plus" onClick={() => navigate("/index/exams/new")} />
        )}
      </div>
      <DataTable value={items} loading={loading} paginator rows={10} stripedRows emptyMessage="No exams found.">
        <Column field="name" header="Name" sortable />
        <Column field="exam_type" header="Type" sortable />
        <Column field="grade.name" header="Grade" />
        <Column field="subject.name" header="Subject" />
        <Column field="exam_date" header="Date" body={dateTemplate} sortable />
        <Column field="total_marks" header="Total" />
        <Column field="passing_marks" header="Pass" />
        <Column header="Status" body={publishedTemplate} />
        <Column header="Actions" body={actionsTemplate} style={{ width: "220px" }} />
      </DataTable>
    </Card>
  );
};

export default ExamListPage;
